import isEmpty from "lodash-es/isEmpty";
import sql from "sql-template-tag";
import { type ActorRefFromLogic, createActor, waitFor } from "xstate";

import { getDb } from "~/data/sql";
import { getDocsApiUrlWithoutProtocol } from "~/state/api/docsapi";
import { LOCAL_WORKSPACE_NAME } from "~/strings";

import { docsApi } from "../../api/docsapi";
import { type AuthenticationMachine, waitForAccessTokenSilently } from "../authentication";
import { createQueryMachine } from "../query.machine";

export const createRemoteRepo = async ({
	input,
}: {
	input: {
		workspaceName: string;
		documentId: string;
		authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;
		skip: boolean;
	};
}) => {
	const { workspaceName, documentId, skip, authenticationActorRef } = input;

	if (skip) {
		return;
	}

	if (isEmpty(workspaceName) || isEmpty(documentId)) {
		throw new Error("Workspace name and document ID are required to create a remote repo");
	}

	if (workspaceName === LOCAL_WORKSPACE_NAME) {
		throw new Error("Cannot create a remote repo for the local workspace");
	}

	const actor = createActor(
		createQueryMachine({
			shouldEnd: true,
			queryKey: `create-remote-repo-${workspaceName}-${documentId}`,
			queryFn: async () => {
				const token = await waitForAccessTokenSilently(input.authenticationActorRef);
				if (!token) {
					throw new Error("Not logged in");
				}

				const authSnap = input.authenticationActorRef.getSnapshot();

				console.debug("Creating remote repo", workspaceName);

				const upstreamWorkspace =
					workspaceName ||
					(authSnap.context.authnStatus === "authenticated" &&
						authSnap.context.user.nickname) ||
					"";
				try {
					return await docsApi.document.createRemote(
						token,
						upstreamWorkspace,
						documentId
					);
				} catch (e) {
					console.error("Failed to create remote repo", e);
					throw e;
				}
			},
			persistData: async () => {
				const authSnap = await waitFor(input.authenticationActorRef, (state) =>
					state.matches("authenticated")
				);
				const upstreamWorkspace =
					workspaceName ||
					(authSnap.context.authnStatus === "authenticated" &&
						authSnap.context.user.nickname) ||
					"";

				const host = getDocsApiUrlWithoutProtocol();
				const slug = upstreamWorkspace;
				const name = upstreamWorkspace;
				const id = `${host}/${slug}`;

				const db = await getDb();

				const query = sql`
						INSERT INTO workspaces (id, host, slug, name) VALUES (${id}, ${host}, ${slug}, ${name})
						ON CONFLICT (id) DO UPDATE SET name = ${name}
					`;
				await db?.execute(query.sql, query.values);

				const query2 = sql`
						INSERT INTO documents_workspaces (documentId, workspaceId) VALUES (${documentId}, ${workspaceName})
						ON CONFLICT (documentId, workspaceId) DO NOTHING
					`;
				await db?.execute(query2.sql, query2.values);
			},
		})
	);
	actor.start();
	return (await waitFor(actor, (state) => state.status === "done")).output;
};
